import type { RunSummaryDto } from '@rw/shared';
import { Link } from 'react-router';
import { StatusPill } from '../../components/ui';
import { formatDistance, formatDuration, formatPace, formatRunWhen, runTitle } from '../../lib/format';
import { useT } from '../../i18n';

/** Strava-style run cards; tapping one opens the run detail. */
export function RunList({ runs }: { runs: RunSummaryDto[] }) {
  const t = useT();
  return (
    <ul className="space-y-2" data-testid="run-list">
      {runs.map((r) => {
        const d = formatDistance(r.distanceM);
        return (
          <li key={r.id}>
            <Link to={`/runs/${r.id}`} className="block rounded-2xl border border-ink-700/80 bg-ink-850 px-4 py-3 active:bg-ink-800">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-display text-lg font-bold leading-tight">{r.title ?? runTitle(r.startedAt)}</div>
                  <div className="text-xs text-ink-400">
                    {formatRunWhen(r.startedAt)}
                    {r.source !== 'LIVE' ? ` · ⌚ ${r.deviceName ?? r.source.toLowerCase()}` : ''}
                  </div>
                </div>
                <StatusPill status={r.status} />
              </div>
              <div className="mt-2 grid grid-cols-4 gap-2 text-sm">
                <Cell label={d.unit} value={r.status === 'PENDING' ? '—' : d.value} />
                <Cell label={t('pace')} value={formatPace(r.paceSecPerKm)} />
                <Cell label={t('time')} value={formatDuration(r.durationS)} />
                <Cell label="xp" value={`+${r.xpAwarded}`} gold />
              </div>
              {r.territoriesCaptured ? (
                <div className="mt-1 text-xs font-semibold text-volt">⬢ {t(r.territoriesCaptured === 1 ? '{n} territory captured' : '{n} territories captured', { n: r.territoriesCaptured })}</div>
              ) : null}
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

function Cell({ label, value, gold }: { label: string; value: string; gold?: boolean }) {
  return (
    <div>
      <div className={`font-display text-lg font-bold tabular ${gold ? 'text-gold' : ''}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-ink-500">{label}</div>
    </div>
  );
}
